import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_FACTS_PT,
  CONTRACT_FACTS_EN,
  FACT_CATEGORY_META,
  type ContractFact,
} from '../data/contractFacts';
import { ChevronLeftIcon, ChevronRightIcon } from './Icons';

const FACT_ROTATION_MS = 9000;
const MANUAL_PAUSE_MS = 15000;
const FADE_MS = 280;

const buildShuffledIndices = (size: number): number[] => {
  const arr = Array.from({ length: size }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

interface ContractProcessingCardProps {
  title: string;
  stepTitle: string;
  stepIcon: React.ReactNode;
  motivation?: string;
}

const ContractProcessingCard: React.FC<ContractProcessingCardProps> = ({
  title,
  stepTitle,
  stepIcon,
  motivation,
}) => {
  const { t, i18n } = useTranslation(['common']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');

  const facts: ContractFact[] = useMemo(
    () => (isEnglish ? CONTRACT_FACTS_EN : CONTRACT_FACTS_PT),
    [isEnglish]
  );

  const order = useRef<number[]>(buildShuffledIndices(facts.length));
  const [cursor, setCursor] = useState<number>(0);
  const [factIndex, setFactIndex] = useState<number>(() => order.current[0] ?? 0);
  const [fading, setFading] = useState<boolean>(false);

  const autoTimer = useRef<number | null>(null);
  const resumeTimer = useRef<number | null>(null);
  const fadeTimer = useRef<number | null>(null);

  const clearAutoTimer = () => {
    if (autoTimer.current !== null) {
      window.clearInterval(autoTimer.current);
      autoTimer.current = null;
    }
  };

  const clearResumeTimer = () => {
    if (resumeTimer.current !== null) {
      window.clearTimeout(resumeTimer.current);
      resumeTimer.current = null;
    }
  };

  const transitionTo = useCallback(
    (next: number) => {
      if (fadeTimer.current !== null) {
        window.clearTimeout(fadeTimer.current);
      }
      setFading(true);
      fadeTimer.current = window.setTimeout(() => {
        if (next >= order.current.length) {
          order.current = buildShuffledIndices(facts.length);
          setCursor(0);
          setFactIndex(order.current[0]);
        } else {
          setCursor(next);
          setFactIndex(order.current[next]);
        }
        setFading(false);
      }, FADE_MS);
    },
    [facts.length]
  );

  const startAutoTimer = useCallback(() => {
    clearAutoTimer();
    if (facts.length <= 1) return;
    autoTimer.current = window.setInterval(() => {
      setCursor((current) => {
        transitionTo(current + 1);
        return current;
      });
    }, FACT_ROTATION_MS);
  }, [facts.length, transitionTo]);

  useEffect(() => {
    order.current = buildShuffledIndices(facts.length);
    setCursor(0);
    setFactIndex(order.current[0] ?? 0);
  }, [facts.length]);

  useEffect(() => {
    startAutoTimer();
    return () => {
      clearAutoTimer();
      clearResumeTimer();
      if (fadeTimer.current !== null) {
        window.clearTimeout(fadeTimer.current);
      }
    };
  }, [startAutoTimer]);

  const handleManualNav = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && cursor === 0) return;
    clearAutoTimer();
    clearResumeTimer();
    const next = direction === 'prev' ? cursor - 1 : cursor + 1;
    transitionTo(next);
    resumeTimer.current = window.setTimeout(() => {
      startAutoTimer();
    }, MANUAL_PAUSE_MS);
  };

  const fact = facts[factIndex] ?? facts[0];
  const meta = FACT_CATEGORY_META[fact.category];
  const label = isEnglish ? meta.enLabel : meta.ptLabel;
  const canGoBack = cursor > 0;

  const prevLabel = t('common:buttons.previous', { defaultValue: isEnglish ? 'Previous' : 'Anterior' });
  const nextLabel = t('common:buttons.next', { defaultValue: isEnglish ? 'Next' : 'Próximo' });

  return (
    <div className="processing-card">
      <div className="processing-title">{title}</div>

      <div className="processing-fact-wrapper">
        <button
          type="button"
          className="processing-fact-nav processing-fact-nav--prev"
          onClick={() => handleManualNav('prev')}
          disabled={!canGoBack}
          aria-label={prevLabel}
          title={prevLabel}
        >
          <ChevronLeftIcon size={18} />
        </button>

        <div
          className={`processing-fact processing-fact--${fact.category} ${
            fading ? 'is-fading' : ''
          }`}
          aria-live="polite"
        >
          <div className="processing-fact-label">
            <span className="processing-fact-emoji" aria-hidden="true">
              {meta.emoji}
            </span>
            <span className="processing-fact-category">{label}</span>
          </div>
          <p className="processing-fact-text">{fact.text}</p>
        </div>

        <button
          type="button"
          className="processing-fact-nav processing-fact-nav--next"
          onClick={() => handleManualNav('next')}
          aria-label={nextLabel}
          title={nextLabel}
        >
          <ChevronRightIcon size={18} />
        </button>
      </div>

      <div className="loading-progress">
        <div className="loading-progress-bar" />
      </div>

      <div className="processing-step-inline">
        <span className="processing-step-inline-icon">{stepIcon}</span>
        <span className="processing-step-inline-title">{stepTitle}</span>
      </div>

      {motivation && (
        <p className="processing-tagline">{motivation}</p>
      )}
    </div>
  );
};

export default ContractProcessingCard;
